/**
 * Delivery escalations (#702): a parcel that failed delivery, went quiet or was
 * lost, counted, chased with the courier and measured.
 *
 * An escalation belongs to a **shipment**, never only to an order: an order can
 * have two shipments (a lost parcel shipped again), and each is escalated on its
 * own. This file is the vocabulary the server writes and the screens read; the
 * rules that open and close escalations live in `apps/api/src/tracking`.
 */

import type { Id, IsoDateTime } from "../primitives";

export const ESCALATION_KINDS = [
  "failed_delivery",
  "no_update",
  "lost",
] as const;
export type EscalationKind = (typeof ESCALATION_KINDS)[number];

export const ESCALATION_KIND_LABELS: Record<EscalationKind, string> = {
  failed_delivery: "Failed delivery",
  no_update: "No update",
  lost: "Lost",
};

/** Failed delivery only: the first, second, and third-or-later attempt. */
export const ESCALATION_LEVELS = [1, 2, 3] as const;
export type EscalationLevel = (typeof ESCALATION_LEVELS)[number];
export const MAX_ESCALATION_LEVEL: EscalationLevel = 3;

/** The level a number of failed attempts stands at; a 4th or later attempt stays at 3. */
export function escalationLevelFor(failedAttempts: number): EscalationLevel {
  if (failedAttempts >= 3) return 3;
  return failedAttempts >= 2 ? 2 : 1;
}

/**
 * `new` has not been sent to the courier at its current level; `sent` has;
 * `resolved` ended on its own or by a person; `dismissed` is Stop tracking.
 */
export const ESCALATION_STATUSES = [
  "new",
  "sent",
  "resolved",
  "dismissed",
] as const;
export type EscalationStatus = (typeof ESCALATION_STATUSES)[number];

export const ESCALATION_STATUS_LABELS: Record<EscalationStatus, string> = {
  new: "New",
  sent: "Sent",
  resolved: "Resolved",
  dismissed: "Dismissed",
};

/** How an escalation ended. */
export const ESCALATION_OUTCOMES = [
  "delivered",
  "rto_delivered",
  "cancelled",
  "order_cancelled",
  "lost",
  "no_longer_stale",
] as const;
export type EscalationOutcome = (typeof ESCALATION_OUTCOMES)[number];

export const ESCALATION_OUTCOME_LABELS: Record<EscalationOutcome, string> = {
  delivered: "Delivered",
  rto_delivered: "Returned to us",
  cancelled: "Shipment cancelled",
  order_cancelled: "Order cancelled",
  lost: "Lost",
  no_longer_stale: "Moving again",
};

/** Why someone stopped tracking an escalation. */
export const ESCALATION_DISMISS_REASONS = [
  "handled_outside",
  "customer_refused",
  "flagged_by_mistake",
  "claim_settled",
] as const;
export type EscalationDismissReason =
  (typeof ESCALATION_DISMISS_REASONS)[number];

export const ESCALATION_DISMISS_REASON_LABELS: Record<
  EscalationDismissReason,
  string
> = {
  handled_outside: "Handled outside the system",
  customer_refused: "Customer refused or no longer wants it",
  flagged_by_mistake: "Flagged by mistake",
  claim_settled: "Lost claim settled",
};

/** What one row of an escalation's history records. */
export const ESCALATION_EVENT_TYPES = [
  "opened",
  "level_raised",
  "sent",
  "resolved",
  "dismissed",
  "reopened",
] as const;
export type EscalationEventType = (typeof ESCALATION_EVENT_TYPES)[number];

export const ESCALATION_SEND_METHODS = ["email", "marked"] as const;
export type EscalationSendMethod = (typeof ESCALATION_SEND_METHODS)[number];

/** Permissions: viewing the screen, and acting on it (send, dismiss, reopen). */
export const VIEW_ESCALATIONS_PERMISSION = "orders.escalations.view";
export const MANAGE_ESCALATIONS_PERMISSION = "orders.escalations.manage";

/**
 * One row of Orders > Escalations: one shipment and kind. An order with two
 * shipments can appear twice, each with its own AWB.
 *
 * Never carries a customer's name, phone or address: the screen, its copy
 * actions, its CSV and every email are built from this and nothing else.
 */
export interface EscalationRow {
  id: Id;
  shipmentId: Id;
  orderId: Id;
  orderNumber: string;
  /** The AWB (tracking number) of this escalation's shipment. */
  awb: string | null;
  shipmentName: string | null;
  courier: string | null;
  courierKey: string | null;
  kind: EscalationKind;
  level: EscalationLevel | null;
  status: EscalationStatus;
  openedAt: IsoDateTime;
  /** Whole days since it opened (or, once closed, until it closed). */
  daysOpen: number;
  lastScanAt: IsoDateTime | null;
  attempts: number;
  lastSentAt: IsoDateTime | null;
  /** The level it was last sent at, so a `new` row can say "sent before at level 1". */
  levelAtLastSend: EscalationLevel | null;
  /** The order's total, a decimal string. */
  orderValue: string;
  currency: string;
  payment: "cod" | "prepaid" | null;
  resolvedAt: IsoDateTime | null;
  outcome: EscalationOutcome | null;
  dismissedReason: EscalationDismissReason | null;
}

export interface EscalationScanEntry {
  happenedAt: IsoDateTime;
  status: string;
  location: string | null;
}

export interface EscalationSendEntry {
  sentAt: IsoDateTime;
  sentByName: string | null;
  level: EscalationLevel | null;
  courier: string | null;
  method: EscalationSendMethod;
  outcome: "sent" | "failed";
  /** Why a send failed, in words that carry no address and no body. */
  reason: string | null;
}

export interface EscalationHistoryEntry {
  at: IsoDateTime;
  type: EscalationEventType;
  level: EscalationLevel | null;
  detail: string | null;
  userName: string | null;
}

/** The detail panel: the row, the scan timeline, every send and the history. */
export interface EscalationDetail extends EscalationRow {
  stage: string | null;
  scans: EscalationScanEntry[];
  sends: EscalationSendEntry[];
  history: EscalationHistoryEntry[];
  dismissedByName: string | null;
  /** Whether the order can still be cancelled (it has not been). */
  orderCancellable: boolean;
  /** The order's last Shopify update, the guard Cancel order sends so it cannot sail over a change. */
  orderChannelUpdatedAt: IsoDateTime;
}

export interface DismissEscalationInput {
  reason: EscalationDismissReason;
  /** Required: why, in a sentence. It is added to the escalation's comments. */
  comment: string;
}

/** How many rows are in each tab, for the tab badges and the navigation count. */
export interface EscalationCounts {
  new: number;
  sent: number;
  resolved: number;
  dismissed: number;
}

/** The escalation count badge shown beside Escalations in the navigation. */
export const ESCALATION_BADGE_STATUS: EscalationStatus = "new";

/** How a shipment stage that ends an escalation ends it; null for a stage that does not. */
export function escalationOutcomeForStage(
  stage: string | null,
): EscalationOutcome | null {
  switch (stage) {
    case "delivered":
      return "delivered";
    case "rto_delivered":
      return "rto_delivered";
    case "cancelled":
      return "cancelled";
    case "lost":
      return "lost";
    default:
      return null;
  }
}

/** Setting the escalation email address and the status email times. */
export const SETTINGS_ESCALATIONS_PERMISSION = "orders.escalations.settings";

/**
 * Where the escalation emails go and when the status email is sent. Times are
 * `HH:MM` in India time.
 */
export interface EscalationEmailSettings {
  address: string;
  morningEnabled: boolean;
  morningTime: string;
  eveningEnabled: boolean;
  eveningTime: string;
}

export const DEFAULT_ESCALATION_EMAIL_SETTINGS: EscalationEmailSettings = {
  address: "astriklabz@gmail.com",
  morningEnabled: true,
  morningTime: "10:00",
  eveningEnabled: true,
  eveningTime: "20:00",
};

export type EscalationEmailSettingsPatch = Partial<EscalationEmailSettings>;

/** Every escalation email subject starts with this, so Gmail can filter on it. */
export const ESCALATION_SUBJECT_PREFIX = "Escalations";

/** One line of the courier email: what is needed, never who the customer is. */
export interface EscalationEmailLine {
  orderNumber: string;
  awb: string | null;
  issue: string;
  daysOpen: number;
}

/** What one courier's email will say, shown before anything is sent. */
export interface EscalationCourierEmail {
  courier: string;
  subject: string;
  /** How many shipments the email lists. */
  count: number;
  lines: EscalationEmailLine[];
  /** The ids of the escalations this email covers. */
  ids: Id[];
}

export interface EscalationEmailPreview {
  /** Where it will go: the Settings address. */
  to: string;
  couriers: EscalationCourierEmail[];
  /** Rows chosen that are not `new` (already sent, closed) and so are left out. */
  skipped: number;
}

export interface EscalationEmailSendResult {
  /** Rows moved to Sent. */
  sent: number;
  /** Rows whose courier's email failed; they stay `new`. */
  failed: number;
  /** Why the failed ones failed, safe to show. */
  reasons: string[];
}

export type EscalationStatusTrigger = "morning" | "evening" | "manual";

/** A status email attempt that did not go out, listed on the screen. */
export interface EscalationMailProblem {
  at: IsoDateTime;
  trigger: EscalationStatusTrigger;
  reason: string;
}

export interface EscalationStatusNowResult {
  ok: boolean;
  newCount: number;
  alreadyCount: number;
  resolvedCount: number;
  reason: string | null;
}

/**
 * The open escalation of one shipment (new or sent), as a row on the Shipments
 * list and an order's shipment card carries it: enough to name it and to open
 * its detail panel. The most serious open kind wins when there are two.
 */
export interface OpenEscalationRef {
  id: Id;
  kind: EscalationKind;
  level: EscalationLevel | null;
}

/** What names an open escalation beside a delivery status: the level for a failed delivery, else the kind. */
export function openEscalationWord(ref: OpenEscalationRef): string {
  return ref.level === null
    ? ESCALATION_KIND_LABELS[ref.kind]
    : `Level ${String(ref.level)}`;
}
